import { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, ShoppingBag, Calendar, Ticket, Shield, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AccountUnderReviewModal from '../../misc/AccountUnderReviewModal';

const actions = [
  {
    id: 'registration',
    title: 'New Registration',
    description: 'Register a new client',
    icon: UserPlus,
    path: '/new-registration',
    gradient: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50',
    iconBg: 'bg-emerald-500',
    hoverBorder: 'hover:border-emerald-300 hover:shadow-emerald-100',
    handleClick: (navigate, isActivated, setShowModal) => {
      if (!isActivated) {
        setShowModal(true);
        return;
      }
      navigate('/new-registration');
    },
  },
  {
    id: 'sale',
    title: 'Start Sale',
    description: 'Sell insurance to a client',
    icon: ShoppingBag,
    path: '/insurance/choose',
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    iconBg: 'bg-blue-500',
    hoverBorder: 'hover:border-blue-300 hover:shadow-blue-100',
    handleClick: (navigate, isActivated, setShowModal) => {
      if (!isActivated) {
        setShowModal(true);
        return;
      }
      navigate('/insurance/choose');
    },
  },
  {
    id: 'event-media',
    title: 'Event & Media Sales',
    description: 'Promote events and conferences',
    icon: Calendar,
    path: '/event-media-sales',
    gradient: 'from-violet-500 to-violet-600',
    bgLight: 'bg-violet-50',
    iconBg: 'bg-violet-500',
    hoverBorder: 'hover:border-violet-300 hover:shadow-violet-100',
    handleClick: (navigate, isActivated, setShowModal) => {
      if (!isActivated) {
        setShowModal(true);
        return;
      }
      navigate('/event-media-sales');
    },
  },
  {
    id: 'purchased-events',
    title: 'My Purchased Events',
    description: 'View client event purchases',
    icon: Ticket,
    path: '/event-media-sales/purchased-events',
    gradient: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50',
    iconBg: 'bg-teal-500',
    hoverBorder: 'hover:border-teal-300 hover:shadow-teal-100',
    handleClick: (navigate, isActivated, setShowModal) => {
      if (!isActivated) {
        setShowModal(true);
        return;
      }
      navigate('/event-media-sales/purchased-events');
    },
  },
  {
    id: 'insurance-purchases',
    title: 'My Insurance Purchases',
    description: 'View policies you sold',
    icon: Shield,
    path: '/insurance/purchases',
    gradient: 'from-blue-500 to-indigo-600',
    bgLight: 'bg-blue-50',
    iconBg: 'bg-blue-500',
    hoverBorder: 'hover:border-blue-300 hover:shadow-blue-100',
    fullWidth: true,
    handleClick: (navigate, isActivated, setShowModal) => {
      if (!isActivated) {
        setShowModal(true);
        return;
      }
      navigate('/insurance/purchases');
    },
  },
];

export default function QuickActions() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [showUnderReviewModal, setShowUnderReviewModal] = useState(false);
  const isActivated = user?.status !== 0;

  return (
    <div className="mb-8">
      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
          <h3 className="text-base font-bold text-gray-900 sm:text-lg">Quick Actions</h3>
          <p className="mt-0.5 text-sm text-gray-500">
            Start a registration, insurance sale, or event booking in one click.
          </p>
        </div>

        {/* Action cards grid */}
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:gap-4 sm:p-5 lg:grid-cols-4">
          {actions.map((action, index) => {
            const Icon = action.icon;
            const isFullWidth = action.fullWidth === true;
            return (
              <motion.button
                key={action.id}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: isFullWidth ? 0 : -2 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => action.handleClick(navigate, isActivated, setShowUnderReviewModal)}
                className={`group flex gap-4 items-center rounded-xl border-2 p-4 text-left shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 ${isFullWidth ? `lg:col-span-4 lg:py-5 lg:px-6 lg:border-blue-200 lg:bg-gradient-to-r lg:from-blue-50/80 lg:to-indigo-50/80 ${action.hoverBorder} hover:shadow-lg` : `border-gray-100 bg-white ${action.hoverBorder} hover:shadow-md`}`}
              >
                {isFullWidth ? (
                  <>
                    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg transition-transform duration-200 group-hover:scale-105 sm:h-16 sm:w-16">
                        <Shield size={32} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block text-base font-bold text-gray-900 sm:text-lg">
                          {action.title}
                        </span>
                        <span className="mt-1 block text-sm text-gray-500">
                          {action.description}
                        </span>
                      </div>
                      <div className="flex flex-shrink-0 items-center justify-center gap-2 self-start rounded-lg bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors group-hover:bg-blue-100 sm:justify-start">
                        View purchases
                        <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${action.iconBg} shadow-md transition-transform duration-200 group-hover:scale-105`}
                    >
                      <Icon size={22} className="text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-gray-900">
                        {action.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {action.description}
                      </span>
                    </div>
                    <ArrowRight
                      size={18}
                      className="flex-shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-500"
                    />
                  </>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      <AccountUnderReviewModal
        isOpen={showUnderReviewModal}
        onGoToDashboard={() => setShowUnderReviewModal(false)}
      />
    </div>
  );
}
